import OilyticsClient from 'api/OilyticsClient';
import map from 'lodash/map';

import {
  Bar,
  DatafeedConfiguration,
  DatafeedErrorCallback,
  HistoryCallback,
  HistoryMetadata,
  IDatafeedChartApi,
  LibrarySymbolInfo,
  OnReadyCallback,
  PeriodParams,
  ResolutionString,
  ResolveCallback,
  SearchSymbolResultItem,
  SearchSymbolsCallback,
  SubscribeBarsCallback,
} from './charting_library/datafeed-api';
import { generateSymbol, makeApiRequest, parseFullSymbol } from './helpers';

const DAILY_RESOLUTIONS: ResolutionString[] = ['1D', '1W', '1M'] as ResolutionString[];
const INTRADAY_RESOLUTIONS: ResolutionString[] = ['1', '5', '15', '30', '60', '240', '1D', '1W', '1M'] as ResolutionString[];

const WS_RECONNECT_DELAY_MS = 3000;

interface SubscriptionHandler {
  id: string;
  callback: SubscribeBarsCallback;
}

interface SubscriptionInfo {
  symbol: string;
  resolution: ResolutionString;
  lastBar: Bar | null;
  handlers: SubscriptionHandler[];
}

/**
 * Calculate the start time of the next bar based on resolution.
 * Following TradingView's pattern for bar aggregation.
 */
function getNextBarTime (barTime: number, resolution: string): number {
  const date = new Date(barTime);

  if (resolution === '1D' || resolution === 'D') {
    date.setUTCDate(date.getUTCDate() + 1);
    date.setUTCHours(0, 0, 0, 0);
  } else if (resolution === '1W' || resolution === 'W') {
    date.setUTCDate(date.getUTCDate() + 7);
  } else if (resolution === '1M' || resolution === 'M') {
    date.setUTCMonth(date.getUTCMonth() + 1);
  } else {
    // Minute-based resolution
    const interval = parseInt(resolution, 10);
    if (!isNaN(interval)) {
      date.setTime(date.getTime() + interval * 60 * 1000);
    }
  }

  return date.getTime();
}

/**
 * Manages WebSocket connection for real-time bar streaming.
 * Based on TradingView's streaming implementation pattern.
 */
class StreamingManager {
  private ws: WebSocket | null = null;
  private channelToSubscription: Map<string, SubscriptionInfo> = new Map();
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private isConnecting = false;
  private hadError = false;
  private pendingSubscriptions: Array<{ symbol: string; resolution: string }> = [];

  /**
   * Get or create WebSocket connection.
   */
  private connect (): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return;
    }

    this.isConnecting = true;
    // Determine WebSocket URL:
    // - In development (port 3000): connect directly to backend on port 5000
    //   (CRA proxy doesn't forward WebSocket upgrades, and port 3000 is used for hot reloading)
    // - In production: replace "app" with "api" in hostname to bypass CloudFront
    //   (CloudFront doesn't support WebSocket, so we connect directly to the ALB)
    const isDev = window.location.port === '3000';
    let wsUrl: string;
    if (isDev) {
      wsUrl = 'ws://localhost:5000/stream-bars';
    } else {
      const host = window.location.host.replace(/^app\./, 'api.');
      wsUrl = `wss://${host}/stream-bars`;
    }
    console.debug('[streaming]: Connecting to', wsUrl);

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.isConnecting = false;
        console.debug('[streaming]: Connected');

        // Send any pending subscriptions
        for (const sub of this.pendingSubscriptions) {
          this.sendSubscribe(sub.symbol, sub.resolution);
        }
        this.pendingSubscriptions = [];

        // Re-subscribe to all active subscriptions (in case of reconnect)
        for (const [, info] of this.channelToSubscription) {
          this.sendSubscribe(info.symbol, info.resolution as string);
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (e) {
          console.error('[streaming]: Failed to parse message', e);
        }
      };

      this.ws.onclose = (event) => {
        this.isConnecting = false;
        if (this.hadError) {
          this.hadError = false;
          console.error(
            `[streaming]: WebSocket error, closed with code ${event.code}` +
            (event.reason ? ` reason "${event.reason}"` : '')
          );
        } else {
          console.debug('[streaming]: Disconnected', event.code, event.reason);
        }
        this.ws = null;
        this.scheduleReconnect();
      };

      this.ws.onerror = () => {
        // The DOM fires onerror with a bare Event (no failure details), so
        // reporting here only feeds Sentry a useless "[object Event]". The
        // onclose that always follows carries code/reason — flag the error
        // and report it there
        this.hadError = true;
      };
    } catch (e) {
      this.isConnecting = false;
      console.error('[streaming]: Failed to connect', e);
      this.scheduleReconnect();
    }
  }

  /**
   * Schedule reconnection attempt.
   */
  private scheduleReconnect (): void {
    if (this.reconnectTimeout) {
      return;
    }

    // Only reconnect if there are active subscriptions
    if (this.channelToSubscription.size > 0) {
      console.debug('[streaming]: Scheduling reconnect in', WS_RECONNECT_DELAY_MS, 'ms');
      this.reconnectTimeout = setTimeout(() => {
        this.reconnectTimeout = null;
        this.connect();
      }, WS_RECONNECT_DELAY_MS);
    }
  }

  /**
   * Handle incoming WebSocket message.
   * Updates existing bar or creates new bar based on time comparison.
   */
  private handleMessage (message: any): void {
    if (message.type === 'bar') {
      const channelString = `${message.symbol}:${message.resolution}`;
      const subscriptionItem = this.channelToSubscription.get(channelString);

      if (!subscriptionItem || !message.bar) {
        return;
      }

      const tradeTime = message.bar.time;
      const tradePrice = message.bar.close;
      const lastBar = subscriptionItem.lastBar;

      // Skip bars older than what we already have (prevents time violation errors)
      // Also skip bars that are more than 1 hour old compared to current time
      // This handles the case where TradingView's internal cache has newer data than our lastBar
      const now = Date.now();
      if (lastBar && tradeTime < lastBar.time) {
        console.debug('[streaming]: Skipping old bar', channelString, 'bar time:', new Date(tradeTime).toISOString(), 'lastBar time:', new Date(lastBar.time).toISOString());
        return;
      }

      let bar: Bar;

      if (!lastBar) {
        // First bar - use as-is from server
        bar = {
          time: tradeTime,
          open: message.bar.open,
          high: message.bar.high,
          low: message.bar.low,
          close: message.bar.close,
          volume: message.bar.volume || 0,
        };
      } else {
        const nextBarTime = getNextBarTime(lastBar.time, subscriptionItem.resolution as string);

        if (tradeTime >= nextBarTime) {
          // New bar period - use the server's bar time (handles gaps across weekends/holidays)
          bar = {
            time: tradeTime,
            open: message.bar.open,
            high: message.bar.high,
            low: message.bar.low,
            close: message.bar.close,
            volume: message.bar.volume || 0,
          };
          console.debug('[streaming]: New bar created for', channelString, 'at', new Date(tradeTime).toISOString());
        } else {
          // Same period - backend sends fully resampled data (OHLCV aggregated from
          // all raw bars in the period), so replace high/low/volume rather than accumulate
          bar = {
            ...lastBar,
            high: message.bar.high,
            low: message.bar.low,
            close: tradePrice,
            volume: message.bar.volume || 0,
          };
        }
      }

      // Update lastBar
      subscriptionItem.lastBar = bar;

      // Notify all handlers
      console.debug('[streaming]: Bar update for', channelString, bar);
      subscriptionItem.handlers.forEach((handler) => handler.callback(bar));

    } else if (message.type === 'connected') {
      console.debug('[streaming]: Connection confirmed, conn_id:', message.conn_id);
    } else if (message.type === 'subscribed') {
      console.debug('[streaming]: Subscribed to', message.symbols, message.resolution);
    } else if (message.type === 'unsubscribed') {
      console.debug('[streaming]: Unsubscribed from', message.symbols, message.resolution);
    } else if (message.type === 'pong') {
      // Heartbeat response, ignore
    }
  }

  /**
   * Send subscribe message to server.
   */
  private sendSubscribe (symbol: string, resolution: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const message = {
        type: 'subscribe',
        symbols: [symbol],
        resolution,
      };
      console.debug('[streaming]: Sending subscribe', message);
      this.ws.send(JSON.stringify(message));
    }
  }

  /**
   * Send unsubscribe message to server.
   */
  private sendUnsubscribe (symbol: string, resolution: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const message = {
        type: 'unsubscribe',
        symbols: [symbol],
        resolution,
      };
      console.debug('[streaming]: Sending unsubscribe', message);
      this.ws.send(JSON.stringify(message));
    }
  }

  /**
   * Subscribe to real-time bar updates for a symbol.
   * Following TradingView's subscribeOnStream pattern.
   */
  subscribeOnStream (
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    onRealtimeCallback: SubscribeBarsCallback,
    subscriberUID: string,
    onResetCacheNeededCallback: () => void,
    lastBar: Bar | undefined
  ): void {
    const channelString = `${symbolKey(symbolInfo)}:${resolution}`;
    console.debug('[streaming]: Subscribe requested for', channelString, 'subscriberUID:', subscriberUID);

    const handler: SubscriptionHandler = {
      id: subscriberUID,
      callback: onRealtimeCallback,
    };

    let subscriptionItem = this.channelToSubscription.get(channelString);

    if (subscriptionItem) {
      // Already subscribed - just add handler
      subscriptionItem.handlers.push(handler);
      // Update lastBar if provided
      if (lastBar) {
        subscriptionItem.lastBar = lastBar;
      }
      console.debug('[streaming]: Added handler to existing subscription', channelString);
      return;
    }

    // New subscription
    subscriptionItem = {
      symbol: symbolKey(symbolInfo),
      resolution,
      lastBar: lastBar || null,
      handlers: [handler],
    };

    this.channelToSubscription.set(channelString, subscriptionItem);

    // Connect if not already connected
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.pendingSubscriptions.push({ symbol: symbolKey(symbolInfo), resolution: resolution as string });
      this.connect();
    } else {
      this.sendSubscribe(symbolKey(symbolInfo), resolution as string);
    }
  }

  /**
   * Unsubscribe from real-time bar updates.
   * Following TradingView's unsubscribeFromStream pattern.
   */
  unsubscribeFromStream (subscriberUID: string): void {
    console.debug('[streaming]: Unsubscribe requested for', subscriberUID);

    // Find the subscription containing this handler
    for (const [channelString, subscriptionItem] of this.channelToSubscription) {
      const handlerIndex = subscriptionItem.handlers.findIndex(
        (handler) => handler.id === subscriberUID
      );

      if (handlerIndex !== -1) {
        // Remove handler
        subscriptionItem.handlers.splice(handlerIndex, 1);

        if (subscriptionItem.handlers.length === 0) {
          // No more handlers - unsubscribe from server
          this.sendUnsubscribe(subscriptionItem.symbol, subscriptionItem.resolution as string);
          this.channelToSubscription.delete(channelString);
          console.debug('[streaming]: Removed subscription for', channelString);
        }
        break;
      }
    }

    // Close connection if no more subscriptions
    if (this.channelToSubscription.size === 0 && this.ws) {
      console.debug('[streaming]: No more subscriptions, closing connection');
      this.ws.close();
      this.ws = null;

      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
    }
  }
}

// Singleton streaming manager instance
const streamingManager = new StreamingManager();

const getConfigurationData = (showIntraday: boolean): DatafeedConfiguration => ({
  supported_resolutions: showIntraday ? INTRADAY_RESOLUTIONS : DAILY_RESOLUTIONS,
});

async function getAllSymbols (): Promise<SearchSymbolResultItem[]> {
  // --- Oilytics API symbols (futures) ---
  const client = await OilyticsClient.create();
  const response = await client.getTradeSymbols({ page: 1 });
  const data = response?.data;
  const oilyticsSymbols: SearchSymbolResultItem[] = [];
  if (!Array.isArray(data)) {
    console.debug('[getAllSymbols]: No results found in Oilytics API response');
  } else {
    data.forEach((item: any) => {
      if (Array.isArray(item.codes)) {
        item.codes.forEach((code: string) => {
          oilyticsSymbols.push({
            symbol: code || '',
            ticker: code || '',
            description: item.name || code || '',
            exchange: item.category || 'Oilytics',
            type: 'futures',
          });
        });
      }
    });
    console.debug(`[getAllSymbols]: Returning ${oilyticsSymbols.length} Oilytics symbols`);
  }
  console.debug(`[getAllSymbols]: Returning ${oilyticsSymbols.length} total symbols (Oilytics only)`);
  return oilyticsSymbols;
}

// The symbol a datafeed request is for: the ticker (the formula), so `name` can carry a display
// name (see symbolNames) without changing what is fetched or streamed.
const symbolKey = (symbolInfo: LibrarySymbolInfo): string => symbolInfo.ticker || symbolInfo.name;

class Datafeed implements IDatafeedChartApi {
  private symbolCustomDescription: string | undefined;
  // Display names by formula, e.g. for overlays whose formula would otherwise be their legend
  // title. A live map: the chart adds names as overlays come and go.
  private symbolNames: Map<string, string> | undefined;
  private showIntraday: boolean;
  // Track last bar per symbol:resolution for streaming
  private lastBarsCache: Map<string, Bar> = new Map();

  constructor (options?: {
          symbolCustomDescription?: string;
          symbolNames?: Map<string, string>;
          showIntraday?: boolean;
    }) {
    this.symbolCustomDescription = options?.symbolCustomDescription;
    this.symbolNames = options?.symbolNames;
    this.showIntraday = options?.showIntraday ?? false;
  }

  onReady = (callback: OnReadyCallback): void => {
    console.debug('[onReady]: Method call');
    const configurationData = getConfigurationData(this.showIntraday);
    setTimeout(() => callback(configurationData));
  };

  searchSymbols = async (
    userInput: string,
    exchange: string,
    symbolType: string,
    onResultReadyCallback: SearchSymbolsCallback
  ): Promise<void> => {
    console.debug('[searchSymbols]: Method call', userInput, exchange, symbolType);
    try {
      const symbols = await getAllSymbols();
      const newSymbols = symbols.filter(symbol => {
        const isExchangeValid = exchange === '' || symbol.exchange === exchange;
        const fullName = `${symbol.exchange}:${symbol.ticker}`;
        const isFullSymbolContainsInput = fullName
          .toLowerCase()
          .indexOf(userInput.toLowerCase()) !== -1;
        return isExchangeValid && isFullSymbolContainsInput;
      });
      onResultReadyCallback(newSymbols);
    } catch (error) {
      console.error('[searchSymbols]: Error', error);
      onResultReadyCallback([]);
    }
  };

  resolveSymbol = async (
    symbolName: string,
    onSymbolResolvedCallback: ResolveCallback,
    onResolveErrorCallback: DatafeedErrorCallback
  ): Promise<void> => {
    console.debug('[resolveSymbol]: Method call', symbolName);
    try {
      // Call the Flask /symbols endpoint to resolve symbol info
      const apiSymbolInfo = await makeApiRequest(`/api/symbols?symbol=${encodeURIComponent(symbolName)}`);
      console.debug('[resolveSymbol]: API symbol info', apiSymbolInfo);
      console.debug('[resolveSymbol]: Subsessions data', JSON.stringify({
        subsession_id: apiSymbolInfo.subsession_id,
        subsessions: apiSymbolInfo.subsessions?.length,
        has_extended_hours: apiSymbolInfo.has_extended_hours,
        has_intraday: apiSymbolInfo.has_intraday,
        supported_resolutions: apiSymbolInfo.supported_resolutions,
      }));
      if (apiSymbolInfo && apiSymbolInfo.name) {
        // Map API response to LibrarySymbolInfo
        // Use the API's has_intraday value to determine available resolutions
        // The showIntraday flag on the Datafeed enables intraday capability,
        // but each symbol's actual intraday support is determined by the API
        const symbolHasIntraday = this.showIntraday && apiSymbolInfo.has_intraday;
        const configurationData = getConfigurationData(symbolHasIntraday);
        const symbolInfo: LibrarySymbolInfo = {
          ticker: apiSymbolInfo.name,
          name: this.symbolNames?.get(symbolName) || apiSymbolInfo.name,
          description: this.symbolNames?.get(symbolName) || this.symbolCustomDescription || apiSymbolInfo.description,
          type: apiSymbolInfo.type,
          session: apiSymbolInfo.session,
          timezone: apiSymbolInfo.timezone,
          exchange: apiSymbolInfo.exchange,
          listed_exchange: apiSymbolInfo.listed_exchange,
          format: apiSymbolInfo.format || 'price',
          minmov: apiSymbolInfo.minmov,
          pricescale: apiSymbolInfo.pricescale,
          has_intraday: symbolHasIntraday,
          intraday_multipliers: symbolHasIntraday ? ['1', '5', '15', '30', '60', '240'] : apiSymbolInfo.intraday_multipliers,
          visible_plots_set: apiSymbolInfo.visible_plots_set || 'c',
          has_weekly_and_monthly: apiSymbolInfo.has_weekly_and_monthly,
          supported_resolutions: apiSymbolInfo.supported_resolutions || configurationData.supported_resolutions,
          volume_precision: apiSymbolInfo.volume_precision,
          data_status: apiSymbolInfo.data_status || 'streaming',
          library_custom_fields: apiSymbolInfo.library_custom_fields || {},
          // Extended hours / subsession support for ETH/RTH shading
          ...(apiSymbolInfo.subsessions && {
            subsession_id: apiSymbolInfo.subsession_id,
            subsessions: apiSymbolInfo.subsessions,
          }),
          ...(apiSymbolInfo.has_extended_hours !== undefined && {
            has_extended_hours: apiSymbolInfo.has_extended_hours,
          }),
        };
        console.debug('[resolveSymbol]: Symbol resolved from API', symbolName);
        onSymbolResolvedCallback(symbolInfo);
        return;
      }
      console.error('[resolveSymbol]: Error from /symbols API, falling back', apiSymbolInfo);
      onResolveErrorCallback(apiSymbolInfo.errmsg || 'Error resolving symbol');

    } catch (error) {
      console.error('[resolveSymbol]: Error from /symbols API, falling back', error);
      onResolveErrorCallback('Error resolving symbol');
    }
  };

  getBars = async (
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    periodParams: PeriodParams,
    onHistoryCallback: HistoryCallback,
    onErrorCallback: DatafeedErrorCallback
  ): Promise<void> => {
    const { from, to } = periodParams;
    console.debug('[getBars]: Method call', symbolInfo, resolution, from, to);

    try {
      const urlParameters = {
        symbol: symbolKey(symbolInfo),
        resolution,
        from: from.toString(),
        to: to.toString(),
      };

      const query = Object.keys(urlParameters)
        .map(name => `${name}=${encodeURIComponent(urlParameters[name as keyof typeof urlParameters])}`)
        .join('&');

      const data = await makeApiRequest(`api/history?${query}`);
      console.debug('[getBars]: Data', data);

      let bars: Bar[] = [];
      let earliestTimestamp: number | null = null;

      if (Array.isArray(data.t) && Array.isArray(data.c) && Array.isArray(data.o) && Array.isArray(data.h) && Array.isArray(data.l)) {
        bars = map(data.t, (time, idx) => ({
          time: time * 1000,
          open: data.o[idx],
          high: data.h[idx],
          low: data.l[idx],
          close: data.c[idx],
          volume: data.v[idx],
        }));
        if (data.t.length > 0) {
          earliestTimestamp = Math.min(...data.t) * 1000;
        }
        console.debug(`[getBars]: Before sorting: ${bars.length} bars, from=${from}, to=${to}`);
        console.debug(`[getBars]: First bar time: ${bars[0]?.time}, Last bar time: ${bars[bars.length - 1]?.time}`);

        // Sort bars in chronological order (oldest to newest) - TradingView requirement
        bars.sort((a, b) => a.time - b.time);

        console.debug(`[getBars]: After sorting: First bar time: ${bars[0]?.time}, Last bar time: ${bars[bars.length - 1]?.time}`);
        console.debug('[getBars]: Sample sorted bars:', bars.slice(0, 3));
      } else if (Array.isArray(data.Data)) {
        const allTimes = data.Data.map((bar: any) => bar.time);
        if (allTimes.length > 0) {
          earliestTimestamp = Math.min(...allTimes) * 1000;
        }
        data.Data.forEach((bar: any) => {
          if (bar.time >= from && bar.time < to) {
            bars = [...bars, {
              time: bar.time * 1000,
              low: bar.low,
              high: bar.high,
              open: bar.open,
              close: bar.close,
            }];
          }
        });
      }

      if (bars.length === 0) {
        onHistoryCallback([], { noData: !data.nextTime, nextTime: data.nextTime });
        return;
      }

      console.debug(`[getBars]: returned ${bars.length} bar(s)`);

      // Cache the last bar for streaming
      if (bars.length > 0) {
        const lastBar = bars[bars.length - 1];
        const cacheKey = `${symbolKey(symbolInfo)}:${resolution}`;
        this.lastBarsCache.set(cacheKey, lastBar);
        console.debug('[getBars]: Cached lastBar for', cacheKey, lastBar);
      }

      onHistoryCallback(bars, { noData: false });
    } catch (error) {
      console.error('[getBars]: Get error', error);
      // For intraday resolutions, return "no data" instead of error
      // since the backend may not support intraday data
      const dailyResolutions = ['1D', '1W', '1M', 'D', 'W', 'M'];
      const isIntradayResolution = !dailyResolutions.includes(String(resolution));
      if (isIntradayResolution) {
        console.debug('[getBars]: Intraday resolution not supported, returning no data');
        onHistoryCallback([], { noData: true });
        return;
      }
      onErrorCallback(error instanceof Error ? error.message : 'Unknown error');
    }
  };

  subscribeBars = (
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    onRealtimeCallback: SubscribeBarsCallback,
    subscriberUID: string,
    onResetCacheNeededCallback: () => void
  ): void => {
    console.debug('[subscribeBars]: Method call with subscriberUID:', subscriberUID);

    // Skip daily+ streaming unless the symbol has intraday data to aggregate
    const dailyResolutions = ['1D', '1W', '1M', 'D', 'W', 'M'];
    if (dailyResolutions.includes(String(resolution))) {
      const hasIntradayData = (symbolInfo as any).library_custom_fields?.has_intraday_data;
      if (!hasIntradayData) {
        console.debug('[subscribeBars]: Skipping streaming for daily resolution (no intraday):', resolution);
        return;
      }
    }

    // Get cached lastBar for this symbol:resolution
    const cacheKey = `${symbolKey(symbolInfo)}:${resolution}`;
    const lastBar = this.lastBarsCache.get(cacheKey);
    console.debug('[subscribeBars]: lastBar from cache for', cacheKey, lastBar);

    streamingManager.subscribeOnStream(
      symbolInfo,
      resolution,
      onRealtimeCallback,
      subscriberUID,
      onResetCacheNeededCallback,
      lastBar
    );
  };

  unsubscribeBars = (subscriberUID: string): void => {
    console.debug('[unsubscribeBars]: Method call with subscriberUID:', subscriberUID);
    streamingManager.unsubscribeFromStream(subscriberUID);
  };
}

export { Datafeed };
export default new Datafeed();
